import React, { useCallback, useEffect, useState } from "react";
import {
    Button,
    Card,
    Combobox,
    comboboxFilterAndLimit,
    Input
} from "@salesforce/design-system-react";
import { CONSTANT_ACCOUNT_MIGRATION } from "../../../../constants/pcsk_admin/accountMigration";
import { fetchMyTeams } from "../../../../services/teams";
import { ToastState } from "../../../../utils/types/toast";
import { TOAST_MESSAGE } from "../../../../constants/toastMessages";
import { HandleError } from "../../../utils/common";
import { fetchMPAOrgUnits } from "../../../../services/orgUnit";
import { accountMigrate } from "../../../../services/accountMigration";
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";

type AccountMigrationCreationFormProps = {
    isActive: boolean;
    toast: ToastState;
    setToast: React.Dispatch<React.SetStateAction<ToastState>>;
};

type MPAInfo = {
    value: string;
    label: string;
    id: string;
    isFalcon: boolean;
};

type ServiceTeam = {
    id: string;
    name: string;
    value: string;
    label: string;
    slack_channel: string;
    members: string[];
}

type MPAOU = {
    id: string;
    name: string;
    value: string;
    label: string;
    mpa: string;
    parentID: string;
    path: string;
}

const AccountMigrationCreation: React.FC<AccountMigrationCreationFormProps> = ({ isActive, toast, setToast }) => {
    const [accountID, setAccountID] = useState("");
    const [accountName, setAccountName] = useState("");
    const [serviceTeamID, setServiceTeamID] = useState("");
    const [serviceTeamIDErrorMessage, setServiceTeamIDErrorMessage] = useState("");
    const [falconInstanceID, setFalconInstanceID] = useState("");
    const [falconDomainID, setFalconDomainID] = useState("");
    const [targetOU, setTargetOU] = useState("");
    const [allMPAOU, setAllMPAOU] = useState<MPAOU[]>([]);
    const [falconMPAAccountIDs, setFalconMPAAccountIDs] = useState<MPAInfo[]>([]);
    const [ouList, setOUList] = useState<any[]>([]);
    const [mpaAccountIDs, setMpaAccountIDs] = useState<any[]>([]);
    const [ouSearchInput, setOuSearchInput] = useState("");
    const [selectedOU, setSelectedOU] = useState<any>([]);
    const [selectedMPA, setSelectedMPA] = useState<any>([]);
    const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(true);
    const [accountIDErrorMessage, setAccountIDErrorMessage] = useState("");
    const [falconInstanceIDErrorMessage, setFalconInstanceIDErrorMessage] = useState("");
    const [falconDomainIDErrorMessage, setFalconDomainIDErrorMessage] = useState("");
    const [isMPAListLoading, setIsMPAListLoading] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState(false);

    // GUID validation function
    const isValidGUID = (guid: string): boolean => {
        // Remove any surrounding whitespace
        guid = guid.trim();
        
        // Allow GUIDs with or without hyphens, with or without curly braces
        const guidRegex = /^(\{)?[0-9a-fA-F]{8}[-]?([0-9a-fA-F]{4}[-]?){3}[0-9a-fA-F]{12}(\})?$/;
        
        // Standard GUID format: 8-4-4-4-12
        const standardGuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/i;
        
        // GUID without hyphens
        const noHyphenGuidRegex = /^[0-9a-fA-F]{32}$/i;
        
        // GUID with curly braces
        const bracesGuidRegex = /^\{[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\}$/i;
        
        return standardGuidRegex.test(guid) || noHyphenGuidRegex.test(guid) || bracesGuidRegex.test(guid);
    };



    const clearSelections = () => {
        setAccountID("");
        setAccountName("");
        setServiceTeamID("");
        setFalconInstanceID("");
        setFalconDomainID("");
        setTargetOU("");
        setSelectedMPA([]);
        setSelectedOU([]);
        setAccountIDErrorMessage("");
        setFalconInstanceIDErrorMessage("");
        setFalconDomainIDErrorMessage("");
    };

    const validateAccountInfo = useCallback(() => {
        return (
            accountID.trim() !== "" &&
            accountName.trim() !== "" &&
            serviceTeamID.trim() !== "" &&
            falconInstanceID.trim() !== "" &&
            falconDomainID.trim() !== "" &&
            selectedMPA.length > 0 &&
            selectedOU.length > 0 &&
            !accountIDErrorMessage &&
            !falconInstanceIDErrorMessage &&
            !falconDomainIDErrorMessage
        );
    }, [accountID, accountName, serviceTeamID, falconInstanceID, falconDomainID, selectedMPA, selectedOU, accountIDErrorMessage, falconInstanceIDErrorMessage, falconDomainIDErrorMessage]);

    useEffect(() => {
        if (isActive) {
            clearSelections();
            fetchServiceTeams();
            fetchAllMPAOUs();
        }
    }, [isActive]);

    useEffect(() => {
        setIsCreateButtonDisabled(!validateAccountInfo());
    }, [validateAccountInfo]);

    const fetchServiceTeams = () => {
        setIsMPAListLoading(true)
        fetchMyTeams().then((data) => {
            const transformedData = data.map((serviceTeam: ServiceTeam) => ({
                id: serviceTeam.name,
                label: serviceTeam.name,
                value: serviceTeam.name,
                slack_channel: serviceTeam.slack_channel,
                members: serviceTeam.members
            }));
            
            const allMPAAccounts = getMPAAccounts();
            setMpaAccountIDs(allMPAAccounts);
            const falconMPAs = allMPAAccounts.filter((mpa: any) => mpa.isFalcon);
            setFalconMPAAccountIDs(falconMPAs);
        }).catch((error) => {
            const toastSetter = {
                setToast: setToast
            };
            HandleError(toastSetter, error, TOAST_MESSAGE.ERROR_UPDATING_SERVICE_TEAM);
        }).finally(() => {
            setIsMPAListLoading(false)
        })
    }

    const getMPAAccounts = () => {
        const ssmMPAMetadata = `${process.env.REACT_APP_PCSK_ONBOARDED_MPA_METADATA}`;
        const MPAList = JSON.parse(ssmMPAMetadata);
        console.log("🚀 AccountMigrationCreation MPAList:", MPAList);
        const result = MPAList.map((account: { id: any; label: any; value: any, isFalcon: any; allowed_service_teams: any; allowed_ou_ids: any}) => ({
            value: account.id,
            label: `${account.id} - ${account.label} - ${account.value}`,
            id: account.id,
            isFalcon: account.isFalcon
        }));
        console.log("🚀 AccountMigrationCreation result:", result);
        return result;
    }

    const fetchAllMPAOUs = () => {
        fetchMPAOrgUnits().then((data) => {
            const transformedData = data.map((mpaOU: MPAOU) => ({
                id: mpaOU.id,
                name: mpaOU.name,
                label: mpaOU.path + " (" + mpaOU.id + ")",
                value: mpaOU.id,
                mpa: mpaOU.mpa,
                parentID: mpaOU.parentID,
                path: mpaOU.path
            }));
            setAllMPAOU(transformedData);
        }).catch((error) => {
            const toastSetter = {
                setToast: setToast
            };
            HandleError(toastSetter, error, TOAST_MESSAGE.ERROR_FETCHING_MPA_OU_LIST);
        })
    }

    const handleAccountIDUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setAccountID(value);
        
        if (value.trim() === "") {
            setAccountIDErrorMessage("");
            return;
        }

        // Check if it's a 12-digit whole number
        const accountIDRegex = /^\d{12}$/;
        if (!accountIDRegex.test(value)) {
            setAccountIDErrorMessage("Account ID must be a 12-digit whole number");
        } else {
            setAccountIDErrorMessage("");
        }
    };

    const handleAccountNameUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountName(event.target.value);
    };

    const handleServiceTeamUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setServiceTeamID(value);
        
        if (value.trim() === "") {
            setServiceTeamIDErrorMessage("");
            return;
        }

        if (!isValidGUID(value)) {
            setServiceTeamIDErrorMessage("Service Team ID must be a valid GUID (e.g., 123e4567-e89b-12d3-a456-426614174000)");
        } else {
            setServiceTeamIDErrorMessage("");
        }
        
    };

    const handleFalconInstanceIDUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFalconInstanceID(value);
        
        if (value.trim() === "") {
            setFalconInstanceIDErrorMessage("");
            return;
        }

        if (!isValidGUID(value)) {
            setFalconInstanceIDErrorMessage("Falcon Instance ID must be a valid GUID (e.g., 123e4567-e89b-12d3-a456-426614174000)");
        } else {
            setFalconInstanceIDErrorMessage("");
        }
    };

    const handleFalconDomainIDUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFalconDomainID(value);
        
        if (value.trim() === "") {
            setFalconDomainIDErrorMessage("");
            return;
        }

        if (!isValidGUID(value)) {
            setFalconDomainIDErrorMessage("Falcon Domain ID must be a valid GUID (e.g., 123e4567-e89b-12d3-a456-426614174000)");
        } else {
            setFalconDomainIDErrorMessage("");
        }
    };

    const handleAccountMigration = async () => {
        setShowSpinner(true);
        try {
            const requestData = {
                aws_account_id: accountID,
                account_name: accountName,
                service_team_id: serviceTeamID,
                falcon_instance_id: falconInstanceID,
                falcon_domain_id: falconDomainID,
                org_account_id: "", // Current MPA - will be fetched by backend
                new_org_account_id: selectedMPA[0]?.id || "",
                target_org_unit_id: targetOU
            };

            // This will be replaced with actual API call
            const response = await accountMigrate(requestData);
            
            setToast({
                isVisible: true,
                message: {
                    heading: "Account Migration Request Submitted Successfully",
                    details: `Client Request Token: ${response.client_request_token || response.crt || 'Generated'}`
                },
                variant: "success",
            });

            clearSelections();
        } catch (error) {
            setToast({
                isVisible: true,
                message: {
                    heading: "Error submitting request",
                    details: "Please try again or contact support if the issue persists"
                },
                variant: "error",
            });
        } finally {
            setShowSpinner(false);
        }
    };

    const fetchOUList = (mpaId: string) => {
        let filteredAllOUs = allMPAOU.filter((mpaOU) => mpaOU.mpa === mpaId)
        if (filteredAllOUs.length > 0) {
            setOUList(filteredAllOUs)
        } else {
            // Show default value as root
            const ouList = [{
                value: "root",
                label: "root",
                id: "root",
            }]
            setOUList(ouList)
        }
    }

    return (
        <Card heading={CONSTANT_ACCOUNT_MIGRATION.NEW_ACCOUNT_REQUEST}>
            <div className="slds-p-around_small">
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_migration_account_id"
                        label={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_ID}
                        required
                        value={accountID}
                        onChange={handleAccountIDUpdate}
                        placeholder="Enter Account ID"
                        errorText={accountIDErrorMessage}
                    />
                </div>
                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_migration_account_name"
                        label={CONSTANT_ACCOUNT_MIGRATION.ACCOUNT_NAME}
                        required
                        value={accountName}
                        onChange={handleAccountNameUpdate}
                        placeholder="Enter Account Name"
                    />
                </div>

                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_service_team_id"
                        label={CONSTANT_ACCOUNT_MIGRATION.SERVICE_TEAM_ID}
                        required
                        value={serviceTeamID}
                        onChange={handleServiceTeamUpdate}
                        placeholder="Enter Service Team ID"
                        errorText={serviceTeamIDErrorMessage}
                    />
                </div>

                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_falcon_instance_id"
                        label={CONSTANT_ACCOUNT_MIGRATION.FALCON_INSTANCE_ID}
                        required
                        value={falconInstanceID}
                        onChange={handleFalconInstanceIDUpdate}
                        placeholder={CONSTANT_ACCOUNT_MIGRATION.FALCON_INSTANCE_ID_PLACEHOLDER}
                        errorText={falconInstanceIDErrorMessage}
                    />
                </div>

                <div className="slds-p-around--xx-small">
                    <Input
                        id="account_falcon_domain_id"
                        label={CONSTANT_ACCOUNT_MIGRATION.FALCON_DOMAIN_ID}
                        required
                        value={falconDomainID}
                        onChange={handleFalconDomainIDUpdate}
                        placeholder={CONSTANT_ACCOUNT_MIGRATION.FALCON_DOMAIN_ID_PLACEHOLDER}
                        errorText={falconDomainIDErrorMessage}
                    />
                </div>

                <div className="slds-p-around--xx-small">
                    <Combobox
                        id="account_falcon_mpa"
                        events={{
                            onSelect: (event: any, data: any) => {
                                setSelectedMPA(data.selection);
                                setSelectedOU([]);
                                setTargetOU("");
                                setOuSearchInput("");
                                fetchOUList(data.selection[0].value)
                            },
                        }}
                        labels={{
                            label: CONSTANT_ACCOUNT_MIGRATION.MPA_NAME,
                            placeholder: CONSTANT_ACCOUNT_MIGRATION.MPA_NAME_PLACEHOLDER,
                        }}
                        required
                        menuPosition="absolute"
                        options={comboboxFilterAndLimit({
                            options: falconMPAAccountIDs,
                            selection: selectedMPA,
                            limit: 10000
                        })}
                        hasMenuSpinner={isMPAListLoading}
                        selection={selectedMPA}
                        variant="readonly"
                    />
                </div>

                <div className="slds-p-around--xx-small">
                    <Combobox
                        id="account_falcon_ou"
                        events={{
                            onSelect: (event: any, data: any) => {
                                setSelectedOU(data.selection);
                                setTargetOU(data.selection[0].value);
                            },

                            onChange: (event: any, data: any) => {
                                setOuSearchInput(data.value);
                            },
                            onRequestRemoveSelectedOption: (event: any, data: any) => {
                                setOuSearchInput("");
                                setSelectedOU([]);
                            },
                        }}
                        labels={{
                            label: CONSTANT_ACCOUNT_MIGRATION.OU_ID,
                            placeholder: CONSTANT_ACCOUNT_MIGRATION.OU_ARN_PLACEHOLDER,
                        }}
                        required
                        menuPosition="absolute"
                        options={comboboxFilterAndLimit({
                            inputValue: ouSearchInput,
                            options: ouList,
                            selection: selectedOU,
                            limit: 10000
                        })}
                        value={ouSearchInput}
                        selection={selectedOU}
                        menuItemVisibleLength={5}
                        variant="inline-listbox"
                    />
                </div>

                <div className="slds-p-top_medium slds-grid slds-grid_vertical-align-center">
                    <Button
                        label="Request Account Migration"
                        variant="brand"
                        onClick={handleAccountMigration}
                        disabled={isCreateButtonDisabled || showSpinner}
                    />
                    {showSpinner && (
                        <div className="slds-m-left_medium">
                            <CustomSpinnerOverlay size={"small"} />
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default AccountMigrationCreation;
